import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtIDR } from "@/lib/format";
import { Plus, Trash2, Pencil, Package, Plane, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const STATUS = {
  packing: { label: "Packing", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  sent_to_indo: { label: "Send to Indo", cls: "bg-blue-50 text-blue-700 border-blue-300" },
  received_indo: { label: "Received at Indo", cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
};
const emptyForm = { box_id: "", note: "", cost_origin_sg: "", cost_sg_indo: "", items: [] };

export default function Boxes() {
  const { event, eventId } = useOutletContext();
  const { user } = useAuth();
  const [boxes, setBoxes] = useState([]);
  const [avail, setAvail] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pickId, setPickId] = useState("");
  const canShip = ["shopper", "admin"].some((r) => user.roles.includes(r));
  const canCost = canShip || user.roles.includes("finance");

  const load = async () => {
    const [b, a] = await Promise.all([api.get(`/events/${eventId}/boxes`), api.get(`/events/${eventId}/box-availability`)]);
    setBoxes(b.data); setAvail(a.data);
  };
  useEffect(() => { load(); }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setEditId(null); setForm(emptyForm); setPickId(""); setOpen(true); };
  const openEdit = (b) => { setEditId(b.id); setForm({ box_id: b.box_id, note: b.note || "", cost_origin_sg: b.cost_origin_sg || "", cost_sg_indo: b.cost_sg_indo || "", items: b.items.map((i) => ({ ...i })) }); setPickId(""); setOpen(true); };

  const remainingFor = (cid) => {
    const a = avail.find((x) => x.catalog_item_id === cid);
    if (!a) return 0;
    const inThisBoxBefore = editId ? (boxes.find((b) => b.id === editId)?.items.find((i) => i.catalog_item_id === cid)?.qty || 0) : 0;
    return a.remaining_qty + inThisBoxBefore;
  };
  const addItem = () => {
    if (!pickId) return;
    if (form.items.some((i) => i.catalog_item_id === pickId)) return toast.error("Barang sudah ada di box ini");
    const a = avail.find((x) => x.catalog_item_id === pickId);
    setForm({ ...form, items: [...form.items, { catalog_item_id: pickId, name: a.name, sku: a.sku, qty: Math.max(1, Math.min(1, remainingFor(pickId))) }] });
    setPickId("");
  };
  const save = async () => {
    if (!form.box_id.trim()) return toast.error("Nomor box wajib diisi");
    if (form.items.length === 0) return toast.error("Pilih minimal 1 barang di dalam box");
    const payload = { box_id: form.box_id.trim(), note: form.note, cost_origin_sg: Number(form.cost_origin_sg) || 0, cost_sg_indo: Number(form.cost_sg_indo) || 0, items: form.items.map((i) => ({ catalog_item_id: i.catalog_item_id, qty: Number(i.qty) || 1 })) };
    try {
      if (editId) await api.put(`/boxes/${editId}`, payload); else await api.post(`/events/${eventId}/boxes`, payload);
      toast.success(editId ? "Box diperbarui" : "Box dibuat"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const setStatus = async (b, status) => {
    try { await api.patch(`/boxes/${b.id}/status`, { status }); toast.success(`${b.box_id}: ${STATUS[status].label}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (b) => {
    if (!window.confirm(`Hapus box ${b.box_id}? Barang di dalamnya kembali menjadi belum dikirim.`)) return;
    try { await api.delete(`/boxes/${b.id}`); toast.success("Box dihapus"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const totalOrigin = boxes.reduce((s, b) => s + (b.cost_origin_sg || 0), 0);
  const totalIndo = boxes.reduce((s, b) => s + (b.cost_sg_indo || 0), 0);
  const unshipped = avail.filter((a) => a.remaining_qty > 0);

  return (
    <div className="space-y-6">
      <PageHeader title="Pengiriman Box ke Indonesia" subtitle={`${boxes.length} box · ongkir asal→SG ${fmtIDR(totalOrigin)} · SG→Indo ${fmtIDR(totalIndo)} (otomatis masuk Biaya & Profit). Isi box dipilih dari barang yang sudah dibeli (Bought).`}>
        {canShip && <Button data-testid="btn-add-box" onClick={openNew} className="gap-1.5 bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4" /> Buat Box</Button>}
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          {boxes.length === 0 && <div className="text-center py-16 text-slate-400 bg-white border rounded-lg">Belum ada box.</div>}
          {boxes.map((b) => (
            <div key={b.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`box-${b.box_id}`}>
              <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 bg-slate-50 border-b">
                <Package className="w-4 h-4 text-slate-500" />
                <span className="font-bold text-slate-900 font-mono">{b.box_id}</span>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${STATUS[b.status]?.cls}`} data-testid={`box-status-${b.box_id}`}>{STATUS[b.status]?.label}</span>
                {b.note && <span className="text-xs text-slate-500">· {b.note}</span>}
                <div className="ml-auto flex items-center gap-1">
                  {canShip && b.status === "packing" && <Button size="sm" variant="outline" className="h-8 gap-1" data-testid={`box-send-${b.box_id}`} onClick={() => setStatus(b, "sent_to_indo")}><Plane className="w-3.5 h-3.5" /> Send to Indo</Button>}
                  {canShip && b.status === "sent_to_indo" && <Button size="sm" className="h-8 gap-1 bg-emerald-700 hover:bg-emerald-800" data-testid={`box-receive-${b.box_id}`} onClick={() => setStatus(b, "received_indo")}><CheckCircle2 className="w-3.5 h-3.5" /> Received at Indo</Button>}
                  {canCost && <button data-testid={`box-edit-${b.box_id}`} className="p-1.5 text-slate-400 hover:text-sky-700" onClick={() => openEdit(b)}><Pencil className="w-4 h-4" /></button>}
                  {canShip && <button data-testid={`box-delete-${b.box_id}`} className="p-1.5 text-rose-400 hover:text-rose-600" onClick={() => remove(b)}><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-0">
                <table className="md:col-span-2 w-full text-sm">
                  <thead><tr className="text-xs uppercase text-slate-500 border-b"><th className="text-left px-4 py-1.5">Barang</th><th className="text-right px-4">Qty</th></tr></thead>
                  <tbody>{b.items.map((i) => <tr key={i.catalog_item_id} className="border-b border-slate-100"><td className="px-4 py-1.5">{i.name} <span className="font-mono text-[10px] text-slate-400">{i.sku}</span></td><td className="px-4 text-right font-mono font-semibold">{i.qty}</td></tr>)}</tbody>
                </table>
                <div className="p-3 text-xs space-y-1 border-l border-slate-100 bg-slate-50/50">
                  <div className="flex justify-between"><span className="text-slate-500">Ongkir Asal→SG</span><span className="font-mono">{fmtIDR(b.cost_origin_sg || 0)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Ongkir SG→Indo</span><span className="font-mono">{fmtIDR(b.cost_sg_indo || 0)}</span></div>
                  {b.sent_at && <div className="text-slate-400">Dikirim {new Date(b.sent_at).toLocaleDateString("id-ID")}</div>}
                  {b.received_at && <div className="text-emerald-700">Diterima {new Date(b.received_at).toLocaleDateString("id-ID")}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4 h-fit" data-testid="unshipped-panel">
          <h3 className="font-bold text-slate-900 mb-1">Belum Masuk Box</h3>
          <p className="text-xs text-slate-500 mb-3">Barang dibeli yang belum dikirim ke Indonesia.</p>
          {unshipped.length === 0 && <div className="text-sm text-emerald-700">Semua barang yang dibeli sudah masuk box.</div>}
          <div className="space-y-1.5">{unshipped.map((a) => (
            <div key={a.catalog_item_id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5" data-testid={`unshipped-${a.sku || a.catalog_item_id.slice(0, 6)}`}>
              <span className="truncate">{a.name} <span className="font-mono text-[10px] text-slate-400">{a.sku}</span></span>
              <span className="font-mono font-bold text-rose-600 shrink-0 ml-2">{a.remaining_qty}<span className="text-slate-400 font-normal text-xs">/{a.purchased_qty}</span></span>
            </div>
          ))}</div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editId ? "Edit Box" : "Buat Box Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nomor Box</Label><Input data-testid="box-id" value={form.box_id} onChange={(e) => setForm({ ...form, box_id: e.target.value })} placeholder="BOX-01" /></div>
              <div><Label>Catatan</Label><Input data-testid="box-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Kardus besar, fragile..." /></div>
              <div><Label>Ongkir Negara Asal → Singapore (IDR)</Label><Input data-testid="box-cost-origin" type="number" min="0" step="1000" value={form.cost_origin_sg} onChange={(e) => setForm({ ...form, cost_origin_sg: e.target.value })} disabled={!canCost} /></div>
              <div><Label>Ongkir Singapore → Indo (IDR)</Label><Input data-testid="box-cost-indo" type="number" min="0" step="1000" value={form.cost_sg_indo} onChange={(e) => setForm({ ...form, cost_sg_indo: e.target.value })} disabled={!canCost} /></div>
            </div>
            <div>
              <Label>Isi Box <span className="text-slate-400 font-normal">(dari barang Bought · angka = belum dikirim)</span></Label>
              <div className="flex gap-2 mt-1">
                <select data-testid="box-pick-item" value={pickId} onChange={(e) => setPickId(e.target.value)} className="flex-1 h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
                  <option value="">— pilih barang —</option>
                  {avail.filter((a) => remainingFor(a.catalog_item_id) > 0 && !form.items.some((i) => i.catalog_item_id === a.catalog_item_id)).map((a) => (
                    <option key={a.catalog_item_id} value={a.catalog_item_id}>{a.name}{a.sku ? ` (${a.sku})` : ""} — sisa {remainingFor(a.catalog_item_id)} pcs</option>
                  ))}
                </select>
                <Button type="button" variant="outline" data-testid="box-add-item" onClick={addItem} disabled={!pickId} className="gap-1"><Plus className="w-4 h-4" /> Tambah</Button>
              </div>
              <div className="mt-2 space-y-1.5">
                {form.items.length === 0 && <div className="text-xs text-slate-400 py-2">Belum ada barang di box.</div>}
                {form.items.map((i, idx) => {
                  const max = remainingFor(i.catalog_item_id);
                  return (
                    <div key={i.catalog_item_id} className="flex items-center gap-2 text-sm rounded border px-2 py-1.5" data-testid={`box-item-row-${idx}`}>
                      <span className="flex-1 truncate">{i.name} <span className="font-mono text-[10px] text-slate-400">{i.sku}</span></span>
                      <span className="text-xs text-slate-400">maks {max}</span>
                      <Input data-testid={`box-item-qty-${idx}`} type="number" min="1" max={max} value={i.qty} onChange={(e) => setForm({ ...form, items: form.items.map((x, j) => j === idx ? { ...x, qty: e.target.value } : x) })} className="w-20 h-8 font-mono" />
                      <button type="button" data-testid={`box-item-remove-${idx}`} onClick={() => setForm({ ...form, items: form.items.filter((_, j) => j !== idx) })} className="text-slate-400 hover:text-rose-600"><X className="w-4 h-4" /></button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-box" onClick={save} className="bg-slate-900 hover:bg-slate-800">{editId ? "Simpan Perubahan" : "Simpan Box"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
